import React from 'react';
import { Sparkles, CheckCircle } from 'lucide-react';

const ShowcaseRow = ({ image, title, titleAccent, desc, bullets, reverse, glowFrom, glowTo }) => (
  <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
    <div className={`${reverse ? 'order-2 md:order-1' : ''}`}>
      {!reverse && (
        <div className="relative group">
          <div className={`absolute inset-0 bg-gradient-to-br ${glowFrom} ${glowTo} rounded-2xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity`}></div>
          <div className="relative transform hover:-translate-y-2 transition-transform duration-300">
            <img
              src={image}
              alt={title}
              className="rounded-2xl w-full shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.4)] transition-shadow"
            />
          </div>
        </div>
      )}
      {reverse && (
        <div>
          <h3 className="text-2xl md:text-3xl font-light mb-4">
            {title} <span className="text-amber-400">{titleAccent}</span>
          </h3>
          <p className="text-lg text-stone-300 mb-6 leading-relaxed">{desc}</p>
          <div className="space-y-3">
            {bullets.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="text-stone-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    <div className={`${reverse ? 'order-1 md:order-2' : ''}`}>
      {reverse ? (
        <div className="relative group">
          <div className={`absolute inset-0 bg-gradient-to-br ${glowFrom} ${glowTo} rounded-2xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity`}></div>
          <div className="relative transform hover:-translate-y-2 transition-transform duration-300">
            <img
              src={image}
              alt={title}
              className="rounded-2xl w-full shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.4)] transition-shadow"
            />
          </div>
        </div>
      ) : (
        <div>
          <h3 className="text-2xl md:text-3xl font-light mb-4">
            {title} <span className="text-amber-400">{titleAccent}</span>
          </h3>
          <p className="text-lg text-stone-300 mb-6 leading-relaxed">{desc}</p>
          <div className="space-y-3">
            {bullets.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="text-stone-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

const ProductShowcase = () => {
  return (
    <section className="py-20 md:py-28 bg-stone-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">Anamaya Awe</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6">
            AI-Powered <span className="text-amber-400">AWE Story Generator</span>
          </h2>
          <p className="text-lg text-stone-300 max-w-3xl mx-auto">
            Transform guest data into memorable personalized experiences with our comprehensive
            suite of AI-powered hospitality tools.
          </p>
        </div>

        <ShowcaseRow
          image="/assets/brochure/performance_dashboard.png"
          title="Real-time"
          titleAccent="Performance Dashboard"
          desc="Get a bird's eye view of your team's WOW performance. Track total WOW actions, coverage rates, top performers, and revenue impact — all in one intuitive dashboard with time-period filters."
          bullets={[
            'WOW coverage tracking with progress visualization',
            'Top performers leaderboard with real-time rankings',
            'Rating to booking correlation & revenue impact analysis'
          ]}
          glowFrom="from-amber-500/30"
          glowTo="to-purple-500/30"
        />

        <ShowcaseRow
          reverse
          image="/assets/brochure/guest_directory.png"
          title="Comprehensive"
          titleAccent="Guest Directory"
          desc="Access your entire guest database with powerful search and filtering. View satisfaction scores, review counts, and preferences at a glance. Identify highly satisfied guests and those needing attention instantly."
          bullets={[
            'Search guests by name, email, or preferences',
            'Sort by satisfaction score or review count',
            'Quick stats: Total guests, highly satisfied, with reviews'
          ]}
          glowFrom="from-purple-500/30"
          glowTo="to-pink-500/30"
        />

        <ShowcaseRow
          image="/assets/brochure/guest_profile_preferences.png"
          title="Deep"
          titleAccent="Guest Profile & Preferences"
          desc='Every guest has a rich profile with their likes, dislikes, habits, complaints, and OTA reviews aggregated in one place. One-click "Generate Awe Story" button creates personalized experiences based on this data.'
          bullets={[
            'Likes & dislikes extracted from reviews automatically',
            'Habits & travel patterns identification',
            'Complaint history with severity & resolution tracking'
          ]}
          glowFrom="from-emerald-500/30"
          glowTo="to-teal-500/30"
        />

        <ShowcaseRow
          reverse
          image="/assets/brochure/hotel_rankings.png"
          title="Competitive"
          titleAccent="Hotel Rankings"
          desc="See how your hotel compares in creating Awe moments. Track your ranking across category, city, state, region, and globally. Benchmark against competitors and identify improvement opportunities."
          bullets={[
            'Rankings by category, city, state, region & global',
            'Your hotel stats: WOW stories, execution score',
            'Achievement badges for top 3 performers'
          ]}
          glowFrom="from-blue-500/30"
          glowTo="to-indigo-500/30"
        />

        <ShowcaseRow
          image="/assets/brochure/voice_assistant.png"
          title="AI-Powered"
          titleAccent="Voice Assistant"
          desc="Staff can simply speak about a guest issue and receive instant AI-powered service recovery suggestions. Hands-free problem solving for your frontline team."
          bullets={[
            'Voice-activated service recovery in seconds',
            'AI suggests immediate action & recovery gestures',
            'Cost estimates and personalization notes included'
          ]}
          glowFrom="from-pink-500/30"
          glowTo="to-purple-500/30"
        />

        {/* Feature 6: Mobile Compatibility - Special phone frame */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="order-2 md:order-1">
            <h3 className="text-2xl md:text-3xl font-light mb-4">
              Fully <span className="text-amber-400">Mobile Compatible App</span>
            </h3>
            <p className="text-lg text-stone-300 mb-6 leading-relaxed">
              Access Anamaya on-the-go from any smartphone — manage guests, create stories, and track performance anywhere.
            </p>
          </div>
          <div className="order-1 md:order-2 relative group flex justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-2xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
            <div className="relative transform hover:-translate-y-2 transition-transform duration-300">
              <div className="relative w-[280px] h-[560px] bg-stone-800 rounded-[3rem] p-3 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)]">
                <div className="w-full h-full rounded-[2.5rem] overflow-hidden bg-white">
                  <img
                    src="/assets/brochure/mobile_compatibility.png"
                    alt="Mobile Compatibility"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute top-5 left-1/2 -translate-x-1/2 w-24 h-6 bg-stone-800 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
